import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mandalaService } from '../../services/mandalaService';
import { MandalaChart, MandalaCell } from '../../types/Mandala';
import PageHeader from '../common/PageHeader';
import './MandalaDetail.css';

interface MandalaDetailProps {
  onToggleSidebar: () => void;
}

const MandalaDetail: React.FC<MandalaDetailProps> = ({ onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mandala, setMandala] = useState<MandalaChart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCellId, setEditingCellId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editColor, setEditColor] = useState('');

  useEffect(() => {
    if (user && id) {
      loadMandala();
    }
  }, [user, id]);

  const loadMandala = async () => {
    if (!user || !id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await mandalaService.getMandalaChartById(id);
      if (response.success && response.chart) {
        setMandala(response.chart);
      } else {
        setError(response.message || 'Failed to load mandala chart');
      }
    } catch (err) {
      console.error('Error loading mandala chart:', err);
      setError('Failed to load mandala chart');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/mandala');
  };

  const handleCellClick = (cell: MandalaCell) => {
    setEditingCellId(cell.id);
    setEditTitle(cell.title);
    setEditDescription(cell.description || '');
    setEditColor(cell.color || '');
  };

  const handleSaveCell = async (cellId: number) => {
    if (!id) return;

    try {
      setError(null);
      const response = await mandalaService.updateCell(id, cellId.toString(), {
        title: editTitle,
        description: editDescription || undefined,
        color: editColor || undefined
      });

      if (response.success) {
        await loadMandala();
        setEditingCellId(null);
        setEditTitle('');
        setEditDescription('');
        setEditColor('');
      } else {
        setError(response.message || 'Failed to update cell');
      }
    } catch (err) {
      console.error('Error updating cell:', err);
      setError('Failed to update cell');
    }
  };

  const handleCancelEdit = () => {
    setEditingCellId(null);
    setEditTitle('');
    setEditDescription('');
    setEditColor('');
  };

  const getCellPositionLabel = (position: number): string => {
    if (position === 0) return 'Center';
    return `Area ${position}`;
  };

  // Calculate position for surrounding cells in a circle
  const getCellPosition = (position: number, radius: number) => {
    if (position === 0) return { x: 0, y: 0 }; // Center
    
    // 8 positions around a circle (positions 1-8)
    const angle = ((position - 1) * 2 * Math.PI) / 8 - Math.PI / 2; // Start from top
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius
    };
  };

  if (loading) {
    return (
      <div className="mandala-detail">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/mandala</h1>}
        />
        <div className="mandala-detail-content">
          <div className="loading">Loading mandala chart...</div>
        </div>
      </div>
    );
  }

  if (error || !mandala) {
    return (
      <div className="mandala-detail">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/mandala</h1>}
          rightActions={
            <button className="mandala-back-btn" onClick={handleBack}>
              ← Back
            </button>
          }
        />
        <div className="mandala-detail-content">
          <div className="error-message">
            {error || 'Mandala chart not found'}
            <button onClick={handleBack} className="mandala-back-link">Go back to list</button>
          </div>
        </div>
      </div>
    );
  }

  // Sort cells by position (0-8)
  const sortedCells = mandala.cells ? [...mandala.cells].sort((a, b) => a.position - b.position) : [];
  const centerCell = sortedCells.find(cell => cell.position === 0);
  const surroundingCells = sortedCells.filter(cell => cell.position > 0);

  const radius = 200; // Distance from center to surrounding cells

  return (
    <div className="mandala-detail">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">{mandala.title}</h1>}
        rightActions={
          <button className="mandala-back-btn" onClick={handleBack}>
            ← Back
          </button>
        }
      />
      
      <div className="mandala-detail-content">
        {mandala.description && (
          <div className="mandala-header">
            <p className="mandala-description">{mandala.description}</p>
          </div>
        )}

        <div className="mandala-chart-container">
          <div className="mandala-chart">
            {/* Center Cell */}
            {centerCell && (
              <div 
                className={`mandala-cell mandala-cell-center ${editingCellId === centerCell.id ? 'editing' : ''}`}
                style={{ backgroundColor: centerCell.color || '#f5f5f5' }}
                onClick={() => !editingCellId && handleCellClick(centerCell)}
              >
                {editingCellId === centerCell.id ? (
                  <div className="cell-edit-form">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Title"
                      className="cell-edit-input"
                      autoFocus
                    />
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className="cell-edit-textarea"
                      rows={3}
                    />
                    <input
                      type="color"
                      value={editColor || '#f5f5f5'}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="cell-edit-color"
                    />
                    <div className="cell-edit-actions">
                      <button 
                        className="cell-save-btn"
                        onClick={() => handleSaveCell(centerCell.id)}
                      >
                        Save
                      </button>
                      <button 
                        className="cell-cancel-btn"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="cell-header">
                      <span className="cell-position">{getCellPositionLabel(centerCell.position)}</span>
                      <span className="cell-progress">{Math.round(centerCell.progress)}%</span>
                    </div>
                    <h3 className="cell-title">{centerCell.title}</h3>
                    {centerCell.description && (
                      <p className="cell-description">{centerCell.description}</p>
                    )}
                    {centerCell.links && centerCell.links.length > 0 && (
                      <div className="cell-links">
                        <span className="links-count">{centerCell.links.length} link(s)</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Surrounding Cells */}
            {surroundingCells.map((cell) => {
              const pos = getCellPosition(cell.position, radius);
              return (
                <div
                  key={cell.id}
                  className={`mandala-cell mandala-cell-surrounding ${editingCellId === cell.id ? 'editing' : ''}`}
                  style={{
                    backgroundColor: cell.color || '#f9f9f9',
                    left: `calc(50% + ${pos.x}px)`,
                    top: `calc(50% + ${pos.y}px)`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  onClick={() => !editingCellId && handleCellClick(cell)}
                >
                  {editingCellId === cell.id ? (
                    <div className="cell-edit-form">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder="Title"
                        className="cell-edit-input"
                        autoFocus
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description (optional)"
                        className="cell-edit-textarea"
                        rows={2}
                      />
                      <input
                        type="color"
                        value={editColor || '#f9f9f9'}
                        onChange={(e) => setEditColor(e.target.value)}
                        className="cell-edit-color"
                      />
                      <div className="cell-edit-actions">
                        <button 
                          className="cell-save-btn"
                          onClick={() => handleSaveCell(cell.id)}
                        >
                          Save
                        </button>
                        <button 
                          className="cell-cancel-btn"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="cell-header">
                        <span className="cell-position">{getCellPositionLabel(cell.position)}</span>
                        <span className="cell-progress">{Math.round(cell.progress)}%</span>
                      </div>
                      <h3 className="cell-title">{cell.title}</h3>
                      {cell.description && (
                        <p className="cell-description">{cell.description}</p>
                      )}
                      {cell.links && cell.links.length > 0 && (
                        <div className="cell-links">
                          <span className="links-count">{cell.links.length} link(s)</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}
      </div>
    </div>
  );
};

export default MandalaDetail;
